import { Card, CardContent } from '@crowdfpl/ui-web/card';
import { Crown } from 'lucide-react';
import { useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackUmamiEvent } from '@/helpers/analytics';
import { formatRatingChange } from '@/helpers/duelCard';
import { isDuelInteractionSuppressed } from '@/helpers/duelInteractionGuard';
import { cn, roundPercentages } from '@/helpers/utils';
import { useDebugMode } from '@/hooks/utils/use-debug-mode';
import { DuelCardHeader } from './DuelCardHeader';
import { DuelCardVoteBar } from './DuelCardVoteBar';
import { DuelPlayerBox } from './DuelPlayerBox';
import type { DuelCardProps, DuelCardState } from './types';

function isEventFromDialogLayer(target: EventTarget | null): boolean {
  const el = target as Element | null;
  if (!el?.closest) return false;
  if (el.closest('[role="dialog"]')) return true;
  if (el.closest('[data-radix-dialog-overlay]')) return true;
  // Fallback for overlay markup when data attribute is missing
  if (el.closest('.fixed.inset-0')) return true;
  return false;
}

export function DuelCard({
  duel,
  onVote,
  isAuthenticated = false,
  isVoting = false,
  selectedPlayerId = null,
  state = 'active',
  playerAPoints,
  playerBPoints,
  trackSource,
  predictionOutcome,
  ratingChange,
  isDuelOfTheWeek = false,
  showDOTWBanner = false,
  solioPredictedA,
  solioPredictedB,
}: DuelCardProps) {
  const [highlightedPlayer, setHighlightedPlayer] = useState<'player1' | 'player2' | null>(null);
  const showDebug = useDebugMode();

  useLayoutEffect(() => {
    setHighlightedPlayer(null);
  }, [duel.id, duel.votes.total_votes, duel.votes.player1_votes, duel.votes.player2_votes]);

  useLayoutEffect(() => () => setHighlightedPlayer(null), []);

  const totalVotes = Math.max(0, duel.votes.total_votes);
  const [p1Pct, p2Pct] = roundPercentages(duel.votes.player1_votes, duel.votes.player2_votes);

  const hasUserVote = isAuthenticated && !!selectedPlayerId;
  const currentState = state as DuelCardState;

  const statsClass =
    totalVotes === 0
      ? 'duel-stats-none'
      : p1Pct >= 45 && p1Pct <= 55
        ? 'duel-stats-close'
        : p1Pct <= 30 || p1Pct >= 70
          ? 'duel-stats-big-gap'
          : 'duel-stats-regular';

  const handlePlayerSelect = (player: 'player1' | 'player2') => {
    if (!onVote || isVoting) return;
    if (['locked', 'partial', 'resolved', 'cancelled'].includes(currentState)) return;

    const playerId = player === 'player1' ? 'player1' : 'player2';

    trackUmamiEvent('duel_card_vote_click', {
      duelId: duel.id,
      side: player,
      trackSource: trackSource || 'unknown',
      isAuthenticated,
    });

    if (isAuthenticated && selectedPlayerId === playerId) {
      return;
    }
    onVote(duel.id, playerId);
  };

  const disabledSelect = isVoting || currentState !== 'active';

  const cardClassName = cn(
    `duel-card duel-state-${currentState} ${statsClass} w-full shadow-sm transition-all duration-300 bg-background`,
    isDuelOfTheWeek
      ? 'bg-gradient-to-br from-gold/10 via-gold-light/5 to-gold/10 border-gold/40 ring-1 ring-gold/20'
      : 'bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20',
  );

  const navigate = useNavigate();

  const goToDuel = () => {
    if (trackSource) trackUmamiEvent('duel_card_click', { source: trackSource, duelId: duel.id, isDuelOfTheWeek });
    navigate(`/duel/${duel.slug}`);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (isDuelInteractionSuppressed()) return;
    if (isEventFromDialogLayer(e.target)) return;
    if ((e.target as Element).closest('[data-rating-impact-chip]')) return;
    goToDuel();
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goToDuel();
    }
  };

  // Result logic
  const hasPoints = playerAPoints !== undefined && playerBPoints !== undefined;
  const player1Won = hasPoints && (playerAPoints ?? 0) > (playerBPoints ?? 0);
  const player2Won = hasPoints && (playerBPoints ?? 0) > (playerAPoints ?? 0);
  const isDraw = hasPoints && playerAPoints === playerBPoints;
  const userVotedPlayer1 = selectedPlayerId === 'player1';
  const userVotedPlayer2 = selectedPlayerId === 'player2';
  const userWon = (userVotedPlayer1 && player1Won) || (userVotedPlayer2 && player2Won);

  const showResultFooter = currentState === 'resolved' && hasUserVote && hasPoints;

  const getResultInfo = () => {
    if (!showResultFooter) return null;

    const ratingChangeValue = formatRatingChange(ratingChange);
    const outcome = predictionOutcome ?? (isDraw ? 'tie' : userWon ? 'correct' : 'incorrect');

    switch (outcome) {
      case 'correct':
        return {
          label: 'Correct',
          ratingChange: ratingChangeValue,
          bgColor: 'bg-success',
          textColor: 'text-success-foreground',
          borderColor: 'border-success',
        };
      case 'incorrect':
        return {
          label: 'Incorrect',
          ratingChange: ratingChangeValue,
          bgColor: 'bg-destructive',
          textColor: 'text-destructive-foreground',
          borderColor: 'border-destructive',
        };
      case 'tie':
        return {
          label: 'Draw',
          ratingChange: ratingChangeValue,
          bgColor: 'bg-warning',
          textColor: 'text-warning-foreground',
          borderColor: 'border-warning',
        };
      case 'no_vote':
        return {
          label: 'No vote',
          ratingChange: ratingChangeValue,
          bgColor: 'bg-muted',
          textColor: 'text-muted-foreground',
          borderColor: 'border-muted',
        };
      case 'voided':
        return {
          label: 'Voided',
          ratingChange: ratingChangeValue,
          bgColor: 'bg-muted',
          textColor: 'text-muted-foreground',
          borderColor: 'border-muted',
        };
      default:
        return {
          label: isDraw ? 'Draw' : userWon ? 'Correct' : 'Incorrect',
          ratingChange: ratingChangeValue,
          bgColor: isDraw ? 'bg-warning' : userWon ? 'bg-success' : 'bg-destructive',
          textColor: isDraw ? 'text-warning-foreground' : userWon ? 'text-success-foreground' : 'text-destructive-foreground',
          borderColor: isDraw ? 'border-warning' : userWon ? 'border-success' : 'border-destructive',
        };
    }
  };

  const resultInfo = getResultInfo();

  return (
    <div className="relative">
      <div
        role="link"
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        className="block cursor-pointer"
      >
        <Card className={cardClassName}>
          <CardContent className="p-2.5">
            {isDuelOfTheWeek && showDOTWBanner && (
              <div className="flex items-center justify-center gap-1.5 py-1.5 -mx-2.5 -mt-2.5 mb-2 bg-gradient-to-r from-gold/20 via-gold/30 to-gold/20 border-b border-gold/30 rounded-t-lg">
                <Crown className="h-3.5 w-3.5 text-gold" />
                <span className="text-xs font-semibold text-gold">Duel of the week</span>
                <span className="text-xs text-gold/60">·</span>
                <span className="text-xs font-semibold text-gold">x2 rating points</span>
              </div>
            )}

            <DuelCardHeader
              totalVotes={totalVotes}
              ratingFactors={duel.ratingFactors}
              state={currentState}
              showResultFooter={showResultFooter}
              resultInfo={resultInfo}
            />

            <DuelCardVoteBar
              p1Pct={p1Pct}
              p2Pct={p2Pct}
              player1={duel.player1}
              player2={duel.player2}
              player1Votes={duel.votes.player1_votes}
              player2Votes={duel.votes.player2_votes}
              state={currentState}
              disabledSelect={disabledSelect}
              onHighlightChange={setHighlightedPlayer}
            />

            <div className="grid grid-cols-2 gap-3">
              <DuelPlayerBox
                player={duel.player1}
                isPlayer1
                duel={duel}
                totalVotes={totalVotes}
                selectedPlayerId={selectedPlayerId}
                state={currentState}
                disabledSelect={disabledSelect}
                highlighted={highlightedPlayer === 'player1'}
                playerWon={player1Won}
                isDraw={isDraw}
                showDebug={showDebug}
                solioPredicted={solioPredictedA}
                onSelect={() => handlePlayerSelect('player1')}
                onHighlightClear={() => setHighlightedPlayer(null)}
              />
              <DuelPlayerBox
                player={duel.player2}
                isPlayer1={false}
                duel={duel}
                totalVotes={totalVotes}
                selectedPlayerId={selectedPlayerId}
                state={currentState}
                disabledSelect={disabledSelect}
                highlighted={highlightedPlayer === 'player2'}
                playerWon={player2Won}
                isDraw={isDraw}
                showDebug={showDebug}
                solioPredicted={solioPredictedB}
                onSelect={() => handlePlayerSelect('player2')}
                onHighlightClear={() => setHighlightedPlayer(null)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

    </div>
  );
}
